import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../lib/config';
import { resolveAvatarUrl } from '../../lib/avatar';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, ArrowLeft } from 'lucide-react';

const QUOTES = {
  sv: ['Små steg varje vecka blir stora steg över tid.', 'Fokus på nästa aktion – inte förra misstaget.', 'Träna med mening. Spela med mod.', 'Bra vanor bygger starka spelare.'],
  en: ['Small steps every week become big steps over time.', 'Focus on the next action, not the last mistake.', 'Train with purpose. Play with courage.', 'Good habits build strong players.'],
};

function useProfileCard(playerId, teamId, enabled = true) {
  const { user, sessionVersion } = useAuth();
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([user?.id, sessionVersion, teamId, playerId, enabled, attempt]);
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !teamId || !playerId || !user?.id) throw new Error('unavailable');
        const response = await fetch(`${API_BASE}/api/players/${encodeURIComponent(playerId)}/profile-card?team_id=${encodeURIComponent(teamId)}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (data.team_id !== teamId || data.player_id !== String(playerId)) throw new Error('context');
        if (!controller.signal.aborted) setState({ key, data });
      } catch (error) {
        if (!controller.signal.aborted) setState({ key, error: true });
      }
    };
    load();
    return () => controller.abort();
  }, [key, enabled, playerId, teamId, user?.id]);
  return { data: state?.key === key ? state.data : null, error: state?.key === key && state.error, retry: () => setAttempt(value => value + 1) };
}

function ProfileFeedback({ error, retry, sv }) {
  return <div className="flex min-h-[180px] flex-col items-center justify-center gap-4 p-6 text-muted-foreground" role="status">
    {error ? <><p>{sv ? 'Spelarprofilen kunde inte öppnas i det valda laget.' : 'The player profile could not be opened in this team.'}</p><Button variant="outline" onClick={retry}>{sv ? 'Försök igen' : 'Try again'}</Button></> : <><Loader2 className="h-5 w-5 animate-spin" /><span>{sv ? 'Hämtar spelarprofil…' : 'Loading player profile…'}</span></>}
  </div>;
}

function TeammateCard({ data, sv, embedded = false }) {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const interval = window.setInterval(() => setQuoteIndex(value => (value + 1) % 4), 7000);
    return () => window.clearInterval(interval);
  }, [paused]);
  const name = data.name || (sv ? 'Spelare' : 'Player');
  const meta = [data.team_name, data.jersey_number != null ? `#${data.jersey_number}` : null, data.position].filter(Boolean);
  return <div className={`relative overflow-hidden bg-card text-card-foreground ${embedded ? '' : 'rounded-xl border border-border'}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/[0.06] via-transparent to-transparent" />
    <div className={`relative grid gap-7 p-6 sm:p-8 lg:grid-cols-[1.15fr_1fr] lg:items-center ${embedded ? 'pr-12 sm:pr-12' : ''}`}>
      <div className="flex min-w-0 items-center gap-5">
        <Avatar className="h-24 w-24 shrink-0 border-2 border-amber-500/25 sm:h-28 sm:w-28">
          <AvatarImage src={resolveAvatarUrl(data.avatar_url)} alt={name} className="object-cover" />
          <AvatarFallback className="bg-amber-500/10 text-2xl text-amber-500">{name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toLocaleUpperCase(sv ? 'sv-SE' : 'en')}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">{sv ? 'Spelarprofil' : 'Player profile'}</p>
          <h2 className="break-words text-2xl font-semibold tracking-tight sm:text-3xl">{name}</h2>
          {meta.length > 0 && <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">{meta.map((item, index) => <React.Fragment key={index}>{index > 0 && <span aria-hidden="true" className="text-border">|</span>}<span>{item}</span></React.Fragment>)}</p>}
        </div>
      </div>
      <div className="border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
        <p className="min-h-[64px] text-lg italic leading-relaxed text-muted-foreground">“{QUOTES[sv ? 'sv' : 'en'][quoteIndex]}”</p>
        <div className="mt-5 flex items-center gap-3 text-[10px] font-semibold tracking-[0.3em] text-amber-500"><span className="h-px w-9 bg-amber-500/60" />TRAINNOVA</div>
      </div>
    </div>
  </div>;
}

export function SquadProfileDialog({ playerId, teamId, onClose }) {
  const { user, activeTeam, sessionVersion } = useAuth();
  const key = JSON.stringify([user?.id, activeTeam?.id, sessionVersion, teamId, playerId]);
  // Keep a selection tied to the exact session/team in which it was opened.
  const [openedKey] = useState(key);
  useEffect(() => { if (openedKey !== key) onClose(); }, [key, openedKey, onClose]);
  if (key !== openedKey) return null;
  return <ResolvedProfileDialog playerId={playerId} teamId={teamId} onClose={onClose} />;
}

function ResolvedProfileDialog({ playerId, teamId, onClose }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const sv = (i18n.language || 'sv').startsWith('sv');
  const { data, error, retry } = useProfileCard(playerId, teamId);
  useEffect(() => {
    if (data?.can_view_development === true) {
      onClose();
      navigate(`/players/${encodeURIComponent(playerId)}`);
    }
  }, [data, playerId, navigate, onClose]);
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent className="w-[calc(100vw-32px)] max-w-[940px] overflow-hidden rounded-xl border-border bg-card p-0 shadow-2xl sm:rounded-xl">
      <DialogTitle className="sr-only">{sv ? 'Spelarprofil' : 'Player profile'}</DialogTitle>
      <DialogDescription className="sr-only">{sv ? 'Spelare i ditt lag' : 'Player in your team'}</DialogDescription>
      {data && !data.can_view_development ? <TeammateCard data={data} sv={sv} embedded /> : <ProfileFeedback error={error} retry={retry} sv={sv} />}
    </DialogContent>
  </Dialog>;
}

function ResolvedProfilePage({ children }) {
  const { playerId } = useParams();
  const { activeTeam } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const sv = (i18n.language || 'sv').startsWith('sv');
  const { data, error, retry } = useProfileCard(playerId, activeTeam?.id);
  if (data?.can_view_development === true) return children;
  return <div className="mx-auto w-full max-w-[1100px] space-y-5 p-4 sm:p-6">
    <Button variant="ghost" onClick={() => navigate('/team')}><ArrowLeft className="mr-2 h-4 w-4" />{sv ? 'Tillbaka till truppen' : 'Back to squad'}</Button>
    {data ? <TeammateCard data={data} sv={sv} /> : <ProfileFeedback error={error} retry={retry} sv={sv} />}
  </div>;
}

export default function PlayerProfileAccess({ children }) {
  const { isCoach, user, activeTeam, sessionVersion } = useAuth();
  const { playerId } = useParams();
  if (isCoach) return children;
  const key = JSON.stringify([user?.id, activeTeam?.id, sessionVersion, playerId]);
  return <ResolvedProfilePage key={key}>{children}</ResolvedProfilePage>;
}
